import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, IconButton } from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import CloseIcon from "@material-ui/icons/Close";
import { useVoice } from "../../context/Voice/VoiceContext";

/**
 * Visible softphone-failure banner (debug mode). When a browser-softphone
 * attempt fails (mic / token / register / connect) we DO NOT auto-dial the
 * agent's phone — instead this banner shows exactly which stage failed and
 * offers a MANUAL "call the phone (Bridge)" fallback. This makes a real
 * softphone failure impossible to hide behind the bridge.
 */
const STAGE_HE = {
  init: "אתחול Softphone",
  mic: "מיקרופון / מקור אודיו",
  token: "קבלת Access Token",
  register: "רישום Device מול Twilio",
  connect: "פתיחת חיבור WebRTC",
  media: "שכבת המדיה",
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    "@keyframes spBannerIn": {
      from: { opacity: 0, transform: "translateY(-8px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    bar: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 14px",
      minHeight: 48,
      flexShrink: 0,
      width: "100%",
      boxSizing: "border-box",
      zIndex: 3,
      background: dark ? "#3a1414" : "#fdecea",
      borderBottom: `1px solid ${dark ? "rgba(255,138,128,0.3)" : "#f5c6c2"}`,
      borderInlineStart: "4px solid #d32f2f",
      color: dark ? "#ffb4ab" : "#a32d2d",
      flexWrap: "wrap",
      animation: "$spBannerIn .26s cubic-bezier(0.2,0.8,0.2,1) both",
    },
    icon: { fontSize: 20, flexShrink: 0 },
    text: { flex: 1, minWidth: 160, fontSize: 13, lineHeight: 1.4 },
    stage: { fontWeight: 700 },
    msg: { opacity: 0.9, fontSize: 12.5 },
  };
});

const SoftphoneErrorBanner = () => {
  const classes = useStyles();
  const { softphoneError, clearSoftphoneError, dialBridgeFallback } = useVoice();
  const ref = useRef(null);
  useEffect(() => {
    if (softphoneError && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [softphoneError]);
  if (!softphoneError) return null;

  const stage = STAGE_HE[softphoneError.stage] || softphoneError.stage || "Softphone";

  return (
    <div className={classes.bar} ref={ref}>
      <ErrorOutlineIcon className={classes.icon} />
      <div className={classes.text}>
        <div className={classes.stage}>חיוג מהדפדפן נכשל — שלב: {stage}</div>
        <div className={classes.msg}>{softphoneError.message}</div>
      </div>
      <Button
        size="small"
        variant="contained"
        startIcon={<PhoneIphoneIcon />}
        onClick={dialBridgeFallback}
        style={{ background: "#d32f2f", color: "#fff" }}
      >
        חייג בטלפון (Bridge)
      </Button>
      <IconButton size="small" onClick={clearSoftphoneError} aria-label="סגור">
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

export default SoftphoneErrorBanner;
